import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Artwork } from '../dashboard/Artwork';
import { usePlayer } from '../../stores/player';
import type { TrackSummary } from '../tracks/TrackList';

/**
 * A row of records, scrolled sideways.
 *
 * ## Why the artwork is the control
 *
 * The chart's list gives a cover 44 pixels and hides it below `sm`, which is
 * most of what made the page feel like a spreadsheet: the one thing an artist
 * actually made to be looked at was the smallest thing on screen. Here the
 * sleeve is the button - you press the record to play the record.
 *
 * ## Horizontal, and honest about it
 *
 * A rail that scrolls has to look like it scrolls or nobody finds the rest of
 * it. The cards are deliberately sized so the fourth is clipped at common
 * widths, which is the only reliable affordance for this: an arrow that appears
 * on hover is invisible to touch, and a scrollbar is invisible on macOS.
 */
export interface RailTrack extends TrackSummary {
  rank?: number | null;
  /** Positive is upward. Null means new to this chart. */
  movement?: number | null;
  /** The viewer's own, shown to them alone. See `discovery_self`. */
  yours?: boolean;
}

export function TrackRail({
  title,
  hint,
  tracks,
  badge = 'movement',
  id,
}: {
  title: string;
  hint?: string;
  tracks: RailTrack[];
  /** What the corner of each sleeve says, if anything. */
  badge?: 'movement' | 'rank' | 'yours' | 'none';
  id: string;
}) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  if (tracks.length === 0) {
    return null;
  }

  return (
    <section className="mt-10" data-rail={id}>
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-ink">{title}</h2>
        {hint && <p className="meta-caps text-faint">{hint}</p>}
      </div>

      {/*
        `-mx-*` with matching padding so the first sleeve sits on the page's
        margin while the row still scrolls edge to edge - otherwise the last
        card butts against the viewport with no breathing room.
      */}
      <div className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-3">
        {tracks.map((track) => {
          const isCurrent = current?.id === track.id;

          return (
            <div
              key={track.id}
              data-rail-card={track.id}
              className="w-[164px] shrink-0 snap-start"
            >
              <div className="group relative">
                <button
                  type="button"
                  aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                  onClick={() => (isCurrent ? toggle() : void play(track, tracks, 'chart'))}
                  className="block w-full overflow-hidden rounded-[var(--radius-panel)] border border-line focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {/*
                    The shared placeholder, not a local one.

                    Most tracks on a young catalogue have no cover, so the
                    fallback is what a visitor mostly sees - and `Artwork`
                    already hashes the title into one of the brand hues so a
                    wall of them reads as a wall of records rather than as a
                    page that failed to load. A second placeholder here would
                    have been a duplicate that drifted.
                  */}
                  <Artwork
                    track={track}
                    className="h-[164px] w-[164px] transition-transform duration-300 group-hover:scale-[1.04]"
                  />

                  {/*
                    The scrim only exists under the play glyph and only on
                    hover, so it never dulls the artwork while somebody is
                    looking at it.
                  */}
                  <span
                    className={`absolute inset-0 flex items-center justify-center bg-black/45 transition-opacity ${
                      isCurrent && playing
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                    }`}
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink">
                      {isCurrent && playing ? (
                        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
                          <rect x="4" y="2.5" width="3" height="11" rx="1" />
                          <rect x="9" y="2.5" width="3" height="11" rx="1" />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 16 16"
                          className="h-4 w-4 translate-x-px"
                          fill="currentColor"
                          aria-hidden
                        >
                          <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                        </svg>
                      )}
                    </span>
                  </span>
                </button>

                <Badge kind={badge} track={track} />
              </div>

              <DrawerLink
                to={`/tracks/${track.id}`}
                className="mt-2 block truncate text-sm font-medium text-ink hover:underline"
              >
                {track.title}
              </DrawerLink>

              {track.owner && (
                <Link
                  to={`/${track.owner.username}`}
                  className="meta-caps block truncate text-faint hover:text-ink"
                >
                  {track.owner.display_name || `@${track.owner.username}`}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The corner mark.
 *
 * One per sleeve at most. Two badges on a 164px square is a busy card, and the
 * thing worth saying differs by rail: a climbing rail is about movement, a new
 * rail has nothing to compare against, and an artist's own tracks are not in
 * the running at all.
 */
function Badge({ kind, track }: { kind: 'movement' | 'rank' | 'yours' | 'none'; track: RailTrack }) {
  if (kind === 'none') return null;

  const base =
    'pointer-events-none absolute left-2 top-2 rounded-[var(--radius-pill)] px-2 py-0.5 font-mono text-[10px] backdrop-blur-sm';

  if (kind === 'yours') {
    return <span className={`${base} bg-black/60 text-accent`}>YOURS</span>;
  }

  if (kind === 'rank') {
    return <span className={`${base} bg-black/60 text-ink`}>#{track.rank}</span>;
  }

  if (track.movement === null || track.movement === undefined) {
    return <span className={`${base} bg-black/60 text-ink`}>NEW</span>;
  }

  if (track.movement === 0) return null;

  const up = track.movement > 0;

  return (
    <span className={`${base} bg-black/60 ${up ? 'text-success' : 'text-faint'}`}>
      {up ? '▲' : '▼'}
      {Math.abs(track.movement)}
    </span>
  );
}
